import { api, unwrap } from '../../shared/api/client';
import type { components, operations } from '../../shared/api/schema.gen';

export type TIssue = components['schemas']['IssueResDto'];
export type TIssueDetail = components['schemas']['IssueDetailResDto'];
export type TIssueList = components['schemas']['IssueListResDto'];
export type TIssueBin = components['schemas']['IssueBinResDto'];
export type TIssueView = NonNullable<
  operations['IssuesController_list']['parameters']['query']
>['view'];

export const listIssues = (
  query: operations['IssuesController_list']['parameters']['query'],
): Promise<TIssueList> => unwrap(api.GET('/v1/issues', { params: { query } }));

// `sourceIds`, not a bare `limit`: the server never truncates an explicit id set.
export const listOpenAuthIssues = (sourceIds: readonly string[]): Promise<TIssueList> =>
  sourceIds.length === 0
    ? Promise.resolve(EMPTY_ISSUE_LIST)
    : listIssues({ view: 'active', errorClass: 'auth_failed', sourceIds: [...sourceIds] });

const EMPTY_ISSUE_LIST: TIssueList = {
  data: [],
  counts: {},
  note: '',
};

// `active` is left out on purpose: it is open + acknowledged and would double-count.
const STATES = ['open', 'acknowledged', 'resolved', 'ignored'] as const;

export const viewCount = (
  counts: TIssueList['counts'] | undefined,
  view: TIssueView,
): number | undefined => {
  if (!counts) return undefined;
  if (view === 'all') return STATES.reduce((sum, state) => sum + (counts[state] ?? 0), 0);
  return counts[view ?? 'active'] ?? 0;
};

export const issueDetail = (id: string): Promise<TIssueDetail> =>
  unwrap(api.GET('/v1/issues/{id}', { params: { path: { id } } }));

export const acknowledgeIssue = (id: string, note?: string): Promise<TIssue> =>
  unwrap(api.POST('/v1/issues/{id}/acknowledge', { params: { path: { id } }, body: { note } }));

export const resolveIssue = (id: string, note?: string): Promise<TIssue> =>
  unwrap(api.POST('/v1/issues/{id}/resolve', { params: { path: { id } }, body: { note } }));

export const ignoreIssue = (
  id: string,
  options: { createRule: boolean; wholeClass: boolean; note?: string },
): Promise<TIssue> =>
  unwrap(api.POST('/v1/issues/{id}/ignore', { params: { path: { id } }, body: options }));

export type TMuteSpan = components['schemas']['MuteIssueReqDto']['for'];

export const muteIssue = (id: string, span: TMuteSpan): Promise<TIssue> =>
  unwrap(api.POST('/v1/issues/{id}/mute', { params: { path: { id } }, body: { for: span } }));

export const unmuteIssue = (id: string): Promise<TIssue> =>
  unwrap(api.DELETE('/v1/issues/{id}/mute', { params: { path: { id } } }));

export const setIssueAutoResolve = (id: string, autoResolve: boolean): Promise<TIssue> =>
  unwrap(api.PATCH('/v1/issues/{id}', { params: { path: { id } }, body: { autoResolve } }));

// 30 days, not the server's 31: `/v1/runs` refuses a window wider than 31 days.
export const LOOKBACK_MS = 30 * 86_400_000;

type TBulkOperationDto = components['schemas']['BulkOperationResDto'];
type TBulkPreviewDto = components['schemas']['BulkOperationPreviewResDto'];

const isPreview = (value: TBulkPreviewDto | TBulkOperationDto): value is TBulkPreviewDto =>
  'estimatedMs' in value;

export const previewIssueRedeliver = async (issue: TIssueDetail): Promise<TBulkPreviewDto> => {
  if (!issue.flowId) throw new Error('Only a flow step has copies to redeliver.');
  const answer = await unwrap(
    api.POST('/v1/flows/{id}/copies/retry', {
      params: { path: { id: issue.flowId } },
      body: {
        dryRun: true,
        limit: 500,
        nodeKey: issue.node?.key,
        from: new Date(new Date(issue.firstSeen).getTime() - LOOKBACK_MS).toISOString(),
        to: new Date(Date.now() + 1000).toISOString(),
      },
    }),
  );
  if (!isPreview(answer)) throw new Error('The dry run started a job instead of counting.');
  return answer;
};

export const startIssueRetry = (id: string, idempotencyKey: string): Promise<TBulkOperationDto> =>
  unwrap(api.POST('/v1/issues/{id}/retry', { params: { path: { id } }, body: { idempotencyKey } }));
